import { defaultServiceLanguage, type ServiceLanguage } from "@albertine/i18n";
import type { MemberDataFirestoreRecord } from "@albertine/types";
import {
    LoginSentinelValueEmail,
    LoginSentinelValuePhoneNumber,
} from "@albertine/types/login";
import { getTenantConfig, type Tenant } from "albertine-shared-web";
import { useEffect, useState } from "react";

export async function getAllowedServiceLanguages(
    member: MemberDataFirestoreRecord,
): Promise<ServiceLanguage[]> {
    const tenantConfig = await getTenantConfig(member.tenant);

    return tenantConfig.allowedServiceLanguages;
}

export async function getAllowedServiceLanguagesByTenant(
    tenant: Tenant,
): Promise<ServiceLanguage[]> {
    return (await getTenantConfig(tenant)).allowedServiceLanguages;
}

export async function getServiceLanguagesForAgentToChange(
    member: MemberDataFirestoreRecord,
): Promise<ServiceLanguage[]> {
    // Agents can't change the language once member has logged in once
    if (member.hasLoggedIn) {
        return member.serviceLanguage
            ? [member.serviceLanguage]
            : [defaultServiceLanguage];
    }

    const allowedServiceLanguages = await getAllowedServiceLanguages(member);

    return allowedServiceLanguages;
}

export function useAllowedServiceLanguages(
    member: MemberDataFirestoreRecord,
): ServiceLanguage[] {
    const [allowedServiceLanguages, setAllowedServiceLanguages] = useState<
        ServiceLanguage[]
    >([defaultServiceLanguage]);

    async function fetchServiceLanguages(
        callback: (serviceLanguages: ServiceLanguage[]) => void,
    ) {
        const allowedLanguages = await getAllowedServiceLanguages(member);
        callback(allowedLanguages);
    }

    useEffect(() => {
        void fetchServiceLanguages((languages) => {
            setAllowedServiceLanguages(languages);
        });
    }, [member]);

    return allowedServiceLanguages;
}

export function isEmailSet(member: { email?: string | null }): boolean {
    return !!member.email && member.email !== LoginSentinelValueEmail;
}

export function isPhoneNumberSet(member: { phoneNumber?: string | null }): boolean {
    return !!member.phoneNumber && member.phoneNumber !== LoginSentinelValuePhoneNumber;
}
